import { datadogLogs } from '@datadog/browser-logs';

import { PKG } from '../analytics/analyticsConsts';
import { setCardDesignSelection } from '../app/acceptScreenSlice';
import {
  MODAL_ENUM,
  requireFullSSN,
  setLoading,
  setShowModal,
} from '../app/applicationScreenSlice';
import {
  setAppStatus,
  setEvaluationRejectReapply,
  setExpiresAt,
  setReApplicationInApp,
  setShowCounterOffer,
} from '../app/applicationStatusSlice';
import { setCardOffer } from '../app/consumerInfoSlice';
import { setMultiCardUser } from '../app/loginSlice';
import { SCREEN, setScreen } from '../app/screenStateSlice';
import { store } from '../app/store';
import {
  applicationEvaluationSignUpStatus,
  applicationStatus,
} from '../assets/consts/const';
import { ANALYTICS } from '../firebase/firebase';
import {
  emitSDKEvent,
  SDK_EVENTS,
  SDK_RESULTS,
  type SetSDKResult,
} from '../utils/sdk';
import { Application, getCardDesignOptions, getRejectionReasons } from './api';
import { GetApplicationEvaluation } from './getApplicationEvaluation';
import { SetBackendLanguage } from './setLanguage';

const DEFAULT_TIMEOUT = 2000;
const MAX_POLLING_TIME_MS = 30000;

interface HandleDisplayStateParams {
  displayState: string;
  application: Application;
  showCounterOffer?: boolean;
  token: string;
  isMultiCard?: boolean;
  deviceID?: string;
  delay?: number;
  signUpStatus?: string;
  startTime?: number;
  setResult: SetSDKResult;
}

export const handleDisplayState = async ({
  displayState,
  application,
  showCounterOffer,
  token,
  isMultiCard,
  deviceID,
  delay,
  signUpStatus,
  startTime = Date.now(),
  setResult,
}: HandleDisplayStateParams) => {
  const sendSDKEventToHost = (decisionStatus: string) => {
    if (decisionStatus === 'REJECTED') {
      setResult(SDK_RESULTS.REJECTED);
      emitSDKEvent({
        event: SDK_EVENTS.REJECTED,
      });
    }
  };

  switch (displayState) {
    case 'NewApplicationWithFullSSN': {
      store.dispatch(setLoading(false));
      store.dispatch(requireFullSSN(true));
      store.dispatch(setScreen(SCREEN.APPLICATION));
      store.dispatch(setMultiCardUser(false));
      return;
    }
    case 'ExistingApplication': {
      sendSDKEventToHost(application!.decision.status);

      store.dispatch(setLoading(false));
      store.dispatch(setScreen(SCREEN.STATUS));
      return;
    }
    case 'GetApplicationEvaluation': {
      store.dispatch(setLoading(true));
      if (
        store.getState().applicationStatusSelector.status !==
        applicationStatus.asyncApplication
      ) {
        store.dispatch(setAppStatus(applicationStatus.asyncApplication));
      }

      let response;
      let err;

      if (isMultiCard) {
        [response, err] = await GetApplicationEvaluation({
          token: token,
          applicationUUID: application.applicationUUID,
          deviceID: deviceID,
        });
      } else {
        [response, err] = await GetApplicationEvaluation({
          token: token,
          applicationUUID: application.applicationUUID,
        });
      }

      await new Promise((f) => setTimeout(f, delay || DEFAULT_TIMEOUT));

      // need to handle 404 and 500 responses as a try again
      if (err !== null && err.code !== 404 && err.code !== 500) {
        ANALYTICS.funcError(PKG, 'handleDisplayState', JSON.stringify(err));
        datadogLogs.logger.error('handleDisplayState', {
          error: JSON.stringify(err),
        });
        store.dispatch(setScreen(SCREEN.ERROR));
        return;
      }

      if (!response?.body?.displayState) {
        ANALYTICS.funcError(
          PKG,
          'handleDisplayState',
          'missing display state from getApplicationEvaluation'
        );
      } else if (!response?.body?.application) {
        ANALYTICS.funcError(
          PKG,
          'handleDisplayState',
          'missing application info from getApplicationEvaluation'
        );
      }

      // Stop recursion if time limit is exceeded
      const now = Date.now();
      const timeElapsed = now - startTime;
      if (timeElapsed >= MAX_POLLING_TIME_MS) {
        store.dispatch(setScreen(SCREEN.ASYNC_APP_TIMEOUT));
        return;
      }

      // use old display state if not returned (i.e 404 / 500 error to try again)
      handleDisplayState({
        displayState: response?.body?.displayState || displayState,
        application: response?.body?.application || application,
        showCounterOffer: response?.body?.showCounterOffer || showCounterOffer,
        token: token,
        isMultiCard: isMultiCard,
        deviceID: deviceID,
        delay: parseInt(response?.headers?.['x-imprint-delay-in-ms'] || '2000'),
        signUpStatus: response?.body?.signUpStatus || signUpStatus,
        startTime,
        setResult,
      });

      return;
    }
    default: {
      sendSDKEventToHost(application!.decision.status);

      store.dispatch(setLoading(false));
      switch (application!.decision.status) {
        case 'PREAPPROVED':
        case 'APPROVED': {
          store.dispatch(setCardOffer(application!.cardOffer));
          store.dispatch(setExpiresAt(application!.decision.expiresAt));
          store.dispatch(setShowModal(MODAL_ENUM.noModal));
          store.dispatch(setScreen(SCREEN.OFFER));
          const [_resp, err] = await SetBackendLanguage();

          if (application.cardOffer?.cardDesignOptions) {
            for (const cardDesignOption of application!.cardOffer
              .cardDesignOptions) {
              const productUUID =
                store.getState().merchantDataSelector.productUUID;
              store.dispatch(
                getCardDesignOptions(cardDesignOption.cardDesignID, productUUID)
              );
            }

            store.dispatch(setCardDesignSelection(true));
          }

          if (err) {
            ANALYTICS.funcError(PKG, 'handleDisplayState', JSON.stringify(err));
          }
          return;
        }
        case 'REJECTED': {
          store.dispatch(
            getRejectionReasons({
              applicationUUID: application.applicationUUID,
            })
          );
          if (
            showCounterOffer &&
            store.getState().featureFlagSelector.featureFlags
              .counterOfferEnabled === 'true'
          ) {
            store.dispatch(setShowCounterOffer(showCounterOffer));
          }
          // to decide if we allow re-apply or not
          switch (signUpStatus) {
            case applicationEvaluationSignUpStatus.rejectedAfterCooldownWindow: {
              store.dispatch(setEvaluationRejectReapply(true));
              store.dispatch(setReApplicationInApp(true));
              break;
            }
            case applicationEvaluationSignUpStatus.rejectedWithinCooldownWindow: {
              store.dispatch(setEvaluationRejectReapply(false));
              break;
            }
          }
          store.dispatch(setAppStatus(applicationStatus.notApproved));
          store.dispatch(setShowModal(MODAL_ENUM.noModal));
          //TODO: should go to AccountStatus, not OTP
          store.dispatch(setScreen(SCREEN.OTPSCREEN));
          return;
        }
        case 'FROZEN': {
          store.dispatch(setExpiresAt(application!.decision.expiresAt));
          store.dispatch(setReApplicationInApp(true));
          store.dispatch(setScreen(SCREEN.CREDIT_FROZEN));
          return;
        }
        case 'PENDING': {
          store.dispatch(setAppStatus(applicationStatus.inReview));
          store.dispatch(setScreen(SCREEN.APPLICATION_IN_REVIEW));
          const [_resp, err] = await SetBackendLanguage();
          if (err) {
            ANALYTICS.funcError(PKG, 'handleDisplayState', JSON.stringify(err));
          }
          return;
        }
        default: {
          datadogLogs.logger.error('unknown application decision status');
          store.dispatch(setScreen(SCREEN.ERROR));
          return;
        }
      }
      return;
    }
  }
};
